/**
 * Color picker inside the mobile sticky bar (snippets/sticky.liquid), kept in sync with the
 * color option of the main variant picker in both directions.
 */
class ColorPickerSticky extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  selectedColor = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('change', this.handleColorChange, { signal });
    this.addEventListener('submit', (event) => event.preventDefault(), { signal });

    // The main picker is morphed in place on every variant change, so listening on the document is enough.
    document.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement) || target.name === 'color-sticky') return;
        if (target.closest('variant-picker[data-template-product-match]')) {
          window.setTimeout(() => this.syncFromMainPicker(), 100);
        }
      },
      { signal }
    );
    document.addEventListener('variant:update', () => this.syncFromMainPicker(), { signal });

    this.dataset.colorPickerId = `sticky-${Date.now()}`;
    this.updateSelectedColor();
    this.syncFromMainPicker();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  get mainColorGroup() {
    const picker = document.querySelector('variant-picker[data-template-product-match]');
    return picker?.querySelector('[data-option-kind="color"]') ?? null;
  }

  /** @param {Event} event */
  handleColorChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.name !== 'color-sticky') return;

    this.selectedColor = input.value;
    this.syncWithMainPicker(this.selectedColor);

    this.dispatchEvent(
      new CustomEvent('color-selected', {
        bubbles: true,
        detail: {
          color: this.selectedColor,
          optionId: input.dataset.optionValueId,
          variantId: input.dataset.variantId,
        },
      })
    );
  };

  updateSelectedColor() {
    const checked = this.querySelector('input[type="radio"]:checked');
    if (checked instanceof HTMLInputElement) this.selectedColor = checked.value;
  }

  getSelectedColor() {
    return this.selectedColor;
  }

  /** @param {string} colorValue */
  selectColor(colorValue) {
    const input = Array.from(this.querySelectorAll('input[name="color-sticky"]')).find(
      (el) => el instanceof HTMLInputElement && el.value === colorValue
    );
    if (input instanceof HTMLInputElement) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  syncFromMainPicker() {
    const checked = this.mainColorGroup?.querySelector('input[type="radio"]:checked');
    if (!(checked instanceof HTMLInputElement)) return;
    const stickyInput = Array.from(this.querySelectorAll('input[name="color-sticky"]')).find(
      (el) => el instanceof HTMLInputElement && el.value === checked.value
    );
    if (stickyInput instanceof HTMLInputElement && !stickyInput.checked) {
      stickyInput.checked = true;
      this.selectedColor = checked.value;
    }
  }

  /** @param {string} colorValue */
  syncWithMainPicker(colorValue) {
    const mainInput = Array.from(this.mainColorGroup?.querySelectorAll('input[type="radio"]') ?? []).find(
      (el) => el instanceof HTMLInputElement && el.value === colorValue
    );
    if (mainInput instanceof HTMLInputElement && !mainInput.checked) {
      mainInput.checked = true;
      mainInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

if (!customElements.get('color-picker-sticky')) {
  customElements.define('color-picker-sticky', ColorPickerSticky);
}
